/**
 * financeIntelligenceApi.js
 *
 * Thin client for the Finance Intelligence endpoints (`/ai/finance-intelligence/*`):
 * cashier anomalies, VAT / NAV check, expense intelligence and supplier prices.
 * Same contract as `businessAiApi`: every export resolves to `{ok, data, error}`
 * (plus `status` on failures, so the cashier tab can tell a 403 from an outage)
 * and NEVER throws. The base URL already carries `/api`.
 *
 * @module helpers/financeIntelligenceApi
 */
import {apiFetch} from './WorkSheetContext';

const BASE = '/ai/finance-intelligence';

/**
 * Scope headers that accompany a call.
 * @param {{restaurantId?: string}} [scope] the active management scope.
 * @returns {Record<string, string>} headers for `apiFetch`.
 */
const scopeHeaders = (scope) => (scope?.restaurantId ? {'x-restaurant-id': String(scope.restaurantId)} : {});

/**
 * Build a query string, skipping empty values (`false` is skipped, `true` is `true`).
 * @param {Record<string, *>} params raw params.
 * @param {{restaurantId?: string}} [scope] active scope (adds `restaurantId`).
 * @returns {string} `''` or `?a=b&c=d`.
 */
const buildQuery = (params, scope) => {
    const query = new globalThis.URLSearchParams();

    if (scope?.restaurantId) query.set('restaurantId', String(scope.restaurantId));
    Object.keys(params || {}).forEach((key) => {
        const value = params[key];

        if (value === undefined || value === null || value === '' || value === false) return;
        query.set(key, String(value));
    });
    const text = query.toString();

    return text ? `?${text}` : '';
};

/**
 * Run one GET and normalize the result.
 * @param {string} path API path (no host).
 * @param {{restaurantId?: string}} scope active scope.
 * @param {string} fallbackError error code used when the thrown error has no message.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null), status: (number|null)}>}
 */
const run = async (path, scope, fallbackError) => {
    try {
        const data = await apiFetch(path, {headers: scopeHeaders(scope), skipCache: true});

        return {ok: true, data: data || null, error: null, status: 200};
    } catch (error) {
        return {ok: false, data: null, error: error?.message || fallbackError, status: Number(error?.status) || null};
    }
};

/**
 * Cashier anomaly overview (admin / owner / manager only; 403 otherwise).
 * @param {{days?: 7|30|90, limit?: number, narrative?: boolean, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null), status: (number|null)}>}
 */
export const getCashierAnomalies = ({days = 7, limit = 25, narrative = false, locale, refresh = false} = {}, scope) => run(
    `${BASE}/cashier-anomalies${buildQuery({days, limit, narrative, locale, refresh}, scope)}`,
    scope,
    'cashier_anomalies_failed',
);

/**
 * One employee's anomaly detail (series + flagged orders).
 * @param {string} userId employee id.
 * @param {{days?: 7|30|90, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null), status: (number|null)}>}
 */
export const getCashierAnomalyDetail = (userId, {days = 7, locale, refresh = false} = {}, scope) => run(
    `${BASE}/cashier-anomalies/${encodeURIComponent(userId)}${buildQuery({days, locale, refresh}, scope)}`,
    scope,
    'cashier_anomaly_detail_failed',
);

/**
 * VAT / NAV invoice check (read-only).
 * @param {{from?: string, to?: string, direction?: 'all'|'inbound'|'outbound', limit?: number, narrative?: boolean, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null), status: (number|null)}>}
 */
export const getVatCheck = ({from, to, direction = 'all', limit = 50, narrative = false, locale, refresh = false} = {}, scope) => run(
    `${BASE}/vat-check${buildQuery({from, to, direction, limit, narrative, locale, refresh}, scope)}`,
    scope,
    'vat_check_failed',
);

/**
 * Expense intelligence.
 * @param {{days?: 30|90|365, suggest?: boolean, narrative?: boolean, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null), status: (number|null)}>}
 */
export const getExpenseIntelligence = ({days = 30, suggest = false, narrative = false, locale, refresh = false} = {}, scope) => run(
    `${BASE}/expenses${buildQuery({days, suggest, narrative, locale, refresh}, scope)}`,
    scope,
    'expense_intelligence_failed',
);

/**
 * Supplier price comparison.
 * @param {{days?: number, minSavingPct?: number, limit?: number, narrative?: boolean, locale?: string, refresh?: boolean}} [params] request params (`refresh` = regenerate and overwrite the stored result).
 * @param {{restaurantId?: string}} [scope] active scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null), status: (number|null)}>}
 */
export const getSupplierPrices = ({days = 90, minSavingPct = 3, limit = 25, narrative = false, locale, refresh = false} = {}, scope) => run(
    `${BASE}/supplier-prices${buildQuery({days, minSavingPct, limit, narrative, locale, refresh}, scope)}`,
    scope,
    'supplier_prices_failed',
);
